import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Textarea } from 'components/ui/textarea';
import { Checkbox } from 'components/ui/checkbox';
import { Switch } from 'components/ui/switch';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { 
  ArrowLeft, User, Shield, CreditCard, Trash2, Save,
  Upload, CheckCircle, Clock, XCircle, FileText, Award, Camera,
  AlertTriangle, Search, DollarSign, Bell, Mail, Calendar, Lock, Eye, EyeOff, Loader2, MessageSquare, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import CalendarIntegration from 'components/CalendarIntegration';
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed } from 'services/pushNotifications';

const SERVICES = [
  "Companionship", "Personal Care", "Medication Reminders",
  "Meal Preparation", "Light Housekeeping", "Transportation",
  "Dementia Care", "Overnight Care", "Respite Care"
];

const LANGUAGES = [
  "English", "Spanish", "Chinese", "Tagalog", "Vietnamese",
  "Korean", "French", "Hindi", "Arabic", "Portuguese",
  "Russian", "Japanese", "German", "Italian", "Polish"
];

export default function Settings() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const updateUser = useAuthStore((s) => s.updateUser);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [caregiverProfile, setCaregiverProfile] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [backgroundCheckStatus, setBackgroundCheckStatus] = useState(null);
  const [backgroundPackages, setBackgroundPackages] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [documentType, setDocumentType] = useState('government_id');
  const [uploadingVerification, setUploadingVerification] = useState(false);
  const [bgCheckDialogOpen, setBgCheckDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('basic');
  const [bgCheckForm, setBgCheckForm] = useState({
    full_name: '',
    date_of_birth: '',
    ssn_last_four: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    consent: false
  });
  const [submittingBgCheck, setSubmittingBgCheck] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [textSize, setTextSize] = useState(() => localStorage.getItem('elderlink_text_size') || 'normal');
  const fileInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [notificationSettings, setNotificationSettings] = useState({
    sms_alerts_enabled: false,
    weekly_summary_enabled: true,
    phone: ''
  });
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    isPushSubscribed().then(setPushEnabled);
  }, []);

  const handlePushToggle = async (checked) => {
    if (checked) {
      const ok = await subscribeToPush();
      if (ok) {
        setPushEnabled(true);
        toast.success('Browser push notifications enabled');
      } else {
        toast.error('Could not enable push notifications. Check browser permissions.');
      }
    } else {
      await unsubscribeFromPush();
      setPushEnabled(false);
      toast.success('Push notifications disabled');
    }
  };

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Avatar state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: '',
    hourly_rate: '',
    experience_years: '',
    city: '',
    state: '',
    zip_code: '',
    services: [],
    languages: []
  });

  const isCaregiver = user?.role === 'independent_caregiver';

  useEffect(() => {
    if (isCaregiver) {
      fetchCaregiverProfile();
      fetchVerificationStatus();
      fetchBackgroundCheckStatus();
      fetchBackgroundPackages();
    }
    fetchNotificationSettings();
  }, [isCaregiver]);

  const fetchNotificationSettings = async () => {
    try {
      const response = await api.get(`/users/notification-settings`);
      setNotificationSettings({
        sms_alerts_enabled: response.data.settings?.sms_alerts_enabled || false,
        weekly_summary_enabled: response.data.settings?.weekly_summary_enabled !== false,
        phone: response.data.settings?.phone || ''
      });
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  };

  const handleSmsToggle = async (enabled) => {
    try {
      await api.put(`/users/notification-settings`, {
        sms_alerts_enabled: enabled,
        phone: notificationSettings.phone || formData.phone
      });
      setNotificationSettings(prev => ({ ...prev, sms_alerts_enabled: enabled }));
      toast.success(enabled ? 'SMS alerts enabled' : 'SMS alerts disabled');
    } catch (error) {
      toast.error('Failed to update SMS settings');
    }
  };

  const handleWeeklySummaryToggle = async (enabled) => {
    try {
      await api.put(`/users/notification-settings`, {
        weekly_summary_enabled: enabled
      });
      setNotificationSettings(prev => ({ ...prev, weekly_summary_enabled: enabled }));
      toast.success(enabled ? 'Weekly summaries enabled' : 'Weekly summaries disabled');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleSendTestSummary = async () => {
    try {
      const response = await api.post(`/shifts/send-weekly-summary`, {});
      if (response.data.emails_sent > 0) {
        toast.success('Weekly summary email sent! Check your inbox.');
      } else {
        toast.info('No shifts found for the past week.');
      }
    } catch (error) {
      toast.error('Failed to send weekly summary');
    }
  };

  const fetchCaregiverProfile = async () => {
    try {
      const response = await api.get(`/caregivers/${user.user_id}`);
      setCaregiverProfile(response.data);
      setFormData(prev => ({
        ...prev,
        bio: response.data.bio || '',
        hourly_rate: response.data.hourly_rate || '',
        experience_years: response.data.experience_years || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        services: response.data.services || [],
        languages: response.data.languages || []
      }));
      setBgCheckForm(prev => ({
        ...prev,
        full_name: user?.name || '',
        address_city: response.data.city || '',
        address_state: response.data.state || '',
        address_zip: response.data.zip_code || ''
      }));
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get(`/verification/status`);
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    }
  };

  const fetchBackgroundCheckStatus = async () => {
    try {
      const response = await api.get(`/background-check/status`);
      setBackgroundCheckStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch background check status:', error);
    }
  };

  const fetchBackgroundPackages = async () => {
    try {
      const response = await api.get(`/background-check/packages`);
      setBackgroundPackages(response.data.packages || {});
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleLanguageToggle = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      // Update profile (name, phone)
      const response = await api.put(`/auth/profile`, {
        name: formData.name,
        phone: formData.phone || null
      });
      
      // Update local user state
      if (response.data.user && updateUser) {
        updateUser(response.data.user);
      }
      
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (isCaregiver) {
        await api.put(`/caregivers/profile`, {
          bio: formData.bio || null,
          hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
          experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zip_code || null,
          services: formData.services,
          languages: formData.languages
        });
      }
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post(`/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      uploadAvatar(file);
    }
  };

  const compressImage = (file, maxSize = 256) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
        else { w = Math.round(w * maxSize / h); h = maxSize; }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadAvatar = async (file) => {
    setUploadingAvatar(true);
    try {
      const base64Data = await compressImage(file);
      const response = await api.put('/auth/profile', { picture: base64Data });

      if (response.data.user && updateUser) {
        updateUser(response.data.user);
      }

      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error('Failed to upload profile photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUploadVerification = async () => {
    if (!selectedFile) {
      toast.error('Please select a document to upload');
      return;
    }

    setUploadingVerification(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result;
        
        await api.post(`/verification/upload`, {
          document_type: documentType,
          document_data: base64Data
        });

        toast.success('Verification document submitted successfully!');
        setSelectedFile(null);
        setPreviewUrl(null);
        fetchVerificationStatus();
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingVerification(false);
    }
  };

  const handleSubmitBackgroundCheck = async () => {
    if (!bgCheckForm.consent) {
      toast.error('You must consent to the background check');
      return;
    }
    if (!bgCheckForm.date_of_birth || !bgCheckForm.ssn_last_four) {
      toast.error('Date of birth and SSN last 4 digits are required');
      return;
    }
    if (bgCheckForm.ssn_last_four.length !== 4 || !/^\d+$/.test(bgCheckForm.ssn_last_four)) {
      toast.error('Please enter exactly 4 digits for SSN');
      return;
    }

    setSubmittingBgCheck(true);
    try {
      await api.post(`/background-check/initiate`, {
        package: selectedPackage,
        full_name: bgCheckForm.full_name,
        date_of_birth: bgCheckForm.date_of_birth,
        ssn_last_four: bgCheckForm.ssn_last_four,
        phone: user?.phone,
        address: {
          street: bgCheckForm.address_street,
          city: bgCheckForm.address_city,
          state: bgCheckForm.address_state,
          zip: bgCheckForm.address_zip
        },
        consent: bgCheckForm.consent
      });

      toast.success('Background check initiated successfully!');
      setBgCheckDialogOpen(false);
      fetchBackgroundCheckStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate background check');
    } finally {
      setSubmittingBgCheck(false);
    }
  };

  const getVerificationBadge = () => {
    const status = verificationStatus?.verification_status;
    const trustLevel = verificationStatus?.trust_level;

    if (trustLevel === 'trusted') {
      return (
        <Badge className="bg-amber-500 text-white">
          <Award className="w-3 h-3 mr-1" />
          Trusted Caregiver
        </Badge>
      );
    }
    if (trustLevel === 'identity_verified' || status === 'approved') {
      return (
        <Badge className="bg-teal-500 text-white">
          <CheckCircle className="w-3 h-3 mr-1" />
          Identity Verified
        </Badge>
      );
    }
    if (status === 'pending') {
      return (
        <Badge className="bg-amber-100 text-amber-700">
          <Clock className="w-3 h-3 mr-1" />
          Verification Pending
        </Badge>
      );
    }
    if (status === 'rejected') {
      return (
        <Badge className="bg-rose-100 text-rose-700">
          <XCircle className="w-3 h-3 mr-1" />
          Verification Rejected
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        <Shield className="w-3 h-3 mr-1" />
        Basic Verified
      </Badge>
    );
  };

  const getBgCheckStatusBadge = () => {
    const status = backgroundCheckStatus?.background_check_status;
    
    if (status === 'clear') {
      return <Badge className="bg-teal-500 text-white"><CheckCircle className="w-3 h-3 mr-1" />Cleared</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
    if (status === 'in_progress') {
      return <Badge className="bg-blue-100 text-blue-700"><Search className="w-3 h-3 mr-1" />In Progress</Badge>;
    }
    if (status === 'flagged') {
      return <Badge className="bg-rose-100 text-rose-700"><AlertTriangle className="w-3 h-3 mr-1" />Flagged</Badge>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Settings</h1>
              <p className="text-xs sm:text-sm text-slate-500">Manage your account and preferences</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={loading} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="save-settings-btn">
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <User className="w-5 h-5" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-slate-200 overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-teal-100 text-teal-700 text-2xl font-bold">
                      {user?.name?.[0] || '?'}
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarSelect}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-teal-500 text-white flex items-center justify-center hover:bg-teal-600 transition-colors disabled:opacity-50"
                  data-testid="change-avatar-btn"
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Camera className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              <div>
                <h3 className="font-medium text-slate-900">{user?.name}</h3>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-xs text-slate-400 capitalize">
                    {user?.role?.replace('_', ' ')}
                  </span>
                  {user?.email_verified ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Email Verified
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Email Not Verified
                    </Badge>
                  )}
                  {isCaregiver && getVerificationBadge()}
                  {isCaregiver && getBgCheckStatusBadge()}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your full name"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-slate-50"
                />
                <p className="text-xs text-slate-500">Email cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>
            
            <Button 
              onClick={handleSaveProfile} 
              disabled={savingProfile}
              className="bg-teal-500 hover:bg-teal-600 text-white"
              data-testid="save-profile-btn"
            >
              {savingProfile ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Profile
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password - Only show if user has password (not OAuth-only) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Lock className="w-5 h-5" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="new-password-settings-input"
                  />
                  <p className="text-xs text-slate-500">At least 8 characters</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="confirm-password-settings-input"
                  />
                </div>
              </div>
              
              {passwordForm.new_password && passwordForm.confirm_password && 
               passwordForm.new_password !== passwordForm.confirm_password && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}
              
              <Button 
                type="submit"
                disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* SMS Notifications Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Bell className="w-5 h-5 text-teal-500" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Browser Push Notifications */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <h4 className="font-medium">Browser Push Notifications</h4>
                <p className="text-sm text-slate-500">
                  Receive instant push notifications in your browser for reminders, messages, and alerts.
                </p>
              </div>
              <Switch
                checked={pushEnabled}
                onCheckedChange={handlePushToggle}
                data-testid="push-notifications-toggle"
              />
            </div>

            {/* Weekly Timesheet Summary */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <h4 className="font-medium">Weekly Timesheet Summary</h4>
                <p className="text-sm text-slate-500">
                  Receive a weekly email summary of all care hours with one-click approval.
                </p>
              </div>
              <Switch
                checked={notificationSettings.weekly_summary_enabled}
                onCheckedChange={(checked) => handleWeeklySummaryToggle(checked)}
                data-testid="weekly-summary-toggle"
              />
            </div>
            {notificationSettings.weekly_summary_enabled && (
              <div className="ml-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSendTestSummary}
                  data-testid="send-test-summary"
                >
                  <Mail className="w-4 h-4" />
                  Send Test Summary Now
                </Button>
              </div>
            )}

            {/* SMS Budget Alerts */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <h4 className="font-medium">SMS Budget Alerts</h4>
                <p className="text-sm text-slate-500">
                  Receive SMS notifications when your budget or hours reach thresholds.
                </p>
              </div>
              <Switch
                checked={notificationSettings.sms_alerts_enabled}
                onCheckedChange={(checked) => handleSmsToggle(checked)}
                data-testid="sms-alerts-toggle"
              />
            </div>
            {notificationSettings.sms_alerts_enabled && !formData.phone && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Add your phone number above to receive SMS alerts.
                </p>
              </div>
            )}
            {notificationSettings.sms_alerts_enabled && formData.phone && (
              <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-sm text-teal-700">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  SMS alerts will be sent to {formData.phone}
                </p>
              </div>
            )}
            
            {/* Link to full notification settings */}
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/notification-preferences')} data-testid="manage-notifications-btn">
                <Bell className="w-4 h-4" />
                All Notification Preferences
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => navigate('/sms-settings')} data-testid="manage-sms-btn">
                <MessageSquare className="w-4 h-4" />
                SMS Alert Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Accessibility & Language */}
        <Card data-testid="accessibility-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Globe className="w-5 h-5 text-indigo-500" />
              Language & Accessibility
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <h4 className="font-medium">Language / Idioma</h4>
                <p className="text-sm text-slate-500">Choose your preferred language</p>
              </div>
              <select
                value={localStorage.getItem('i18nextLng')?.substring(0, 2) || 'en'}
                onChange={(e) => { window.location.reload(); localStorage.setItem('i18nextLng', e.target.value); }}
                className="border rounded-md px-3 py-2 text-sm"
                data-testid="settings-language-select"
                aria-label="Select language"
              >
                <option value="en">English</option>
                <option value="es">Espanol</option>
              </select>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 bg-slate-50 rounded-xl">
              <div className="min-w-0">
                <h4 className="font-medium text-sm">Text Size</h4>
                <p className="text-xs text-slate-500">Adjust for readability</p>
              </div>
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1 shrink-0" style={{ fontSize: '0.875rem' }} data-testid="settings-text-size">
                {[['normal', 'A'], ['large', 'A+'], ['xl', 'A++']].map(([size, label]) => (
                  <button
                    key={size}
                    onClick={() => {
                      localStorage.setItem('elderlink_text_size', size);
                      document.documentElement.style.fontSize = size === 'large' ? '112.5%' : size === 'xl' ? '125%' : '100%';
                      setTextSize(size);
                    }}
                    className={`px-2.5 py-1 font-medium rounded-md transition-colors ${textSize === size ? 'bg-teal-500 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                    style={{ fontSize: '0.875rem' }}
                    data-testid={`settings-text-${size}`}
                    aria-label={`Text size ${label}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Google Calendar Integration */}
        <Card className="border-2 border-teal-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Calendar className="w-5 h-5 text-teal-600" />
              Calendar Integration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CalendarIntegration />
          </CardContent>
        </Card>

        {/* Background Check Section - Only for Caregivers */}
        {isCaregiver && (
          <Card className="border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <Search className="w-5 h-5 text-blue-600" />
                Background Check
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {backgroundCheckStatus?.has_clear_check ? (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-teal-800">Background Check Cleared</h4>
                      <p className="text-sm text-teal-600">
                        Completed on {backgroundCheckStatus?.background_check?.completed_at ? 
                          new Date(backgroundCheckStatus.background_check.completed_at).toLocaleDateString() : 'N/A'}
                      </p>
                      <p className="text-sm text-teal-600">
                        Package: {backgroundCheckStatus?.background_check?.package_name}
                      </p>
                    </div>
                  </div>
                </div>
              ) : backgroundCheckStatus?.background_check?.status === 'pending' || 
                   backgroundCheckStatus?.background_check?.status === 'in_progress' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800">Background Check {backgroundCheckStatus?.background_check?.status === 'in_progress' ? 'In Progress' : 'Pending'}</h4>
                      <p className="text-sm text-blue-600">
                        Package: {backgroundCheckStatus?.background_check?.package_name}
                      </p>
                      <p className="text-sm text-blue-600">
                        Estimated completion: {backgroundCheckStatus?.background_check?.estimated_completion ? 
                          new Date(backgroundCheckStatus.background_check.estimated_completion).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : backgroundCheckStatus?.background_check?.status === 'flagged' ? (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-rose-500 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-rose-800">Background Check Flagged</h4>
                      <p className="text-sm text-rose-600">Please contact support for more information.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="font-medium text-slate-900 mb-2">Why Get a Background Check?</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500" />
                        Build trust with families - show you have nothing to hide
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500" />
                        Required for the "Trusted Caregiver" badge
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500" />
                        Get more job opportunities and higher rates
                      </li>
                    </ul>
                  </div>

                  <Dialog open={bgCheckDialogOpen} onOpenChange={setBgCheckDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="start-bg-check-btn">
                        <Search className="w-4 h-4" />
                        Start Background Check
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Background Check Application</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-6 pt-4">
                        {/* Package Selection */}
                        <div className="space-y-3">
                          <Label className="text-base font-medium">Select Package</Label>
                          <div className="grid gap-3">
                            {Object.entries(backgroundPackages).map(([key, pkg]) => (
                              <label
                                key={key}
                                className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                  selectedPackage === key
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="package"
                                  value={key}
                                  checked={selectedPackage === key}
                                  onChange={(e) => setSelectedPackage(e.target.value)}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-slate-900">{pkg.name}</span>
                                    <span className="text-lg font-bold text-blue-600">${pkg.price}</span>
                                  </div>
                                  <p className="text-sm text-slate-500 mt-1">
                                    Processing time: {pkg.processing_days} business days
                                  </p>
                                  <ul className="mt-2 text-sm text-slate-600">
                                    {pkg.includes?.map((item, i) => (
                                      <li key={i} className="flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3 text-teal-500" />
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Personal Information */}
                        <div className="space-y-4">
                          <Label className="text-base font-medium">Personal Information</Label>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Full Legal Name</Label>
                              <Input
                                value={bgCheckForm.full_name}
                                onChange={(e) => setBgCheckForm({ ...bgCheckForm, full_name: e.target.value })}
                                placeholder="John Michael Doe"
                                data-testid="bg-full-name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Date of Birth</Label>
                              <Input
                                type="date"
                                value={bgCheckForm.date_of_birth}
                                onChange={(e) => setBgCheckForm({ ...bgCheckForm, date_of_birth: e.target.value })}
                                data-testid="bg-dob"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>SSN Last 4 Digits</Label>
                              <Input
                                type="text"
                                maxLength={4}
                                value={bgCheckForm.ssn_last_four}
                                onChange={(e) => setBgCheckForm({ ...bgCheckForm, ssn_last_four: e.target.value.replace(/\D/g, '') })}
                                placeholder="1234"
                                data-testid="bg-ssn"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Address */}
                        <div className="space-y-4">
                          <Label className="text-base font-medium">Current Address</Label>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>Street Address</Label>
                              <Input
                                value={bgCheckForm.address_street}
                                onChange={(e) => setBgCheckForm({ ...bgCheckForm, address_street: e.target.value })}
                                placeholder="123 Main St, Apt 4"
                              />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <Label>City</Label>
                                <Input
                                  value={bgCheckForm.address_city}
                                  onChange={(e) => setBgCheckForm({ ...bgCheckForm, address_city: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>State</Label>
                                <Input
                                  value={bgCheckForm.address_state}
                                  onChange={(e) => setBgCheckForm({ ...bgCheckForm, address_state: e.target.value })}
                                  maxLength={2}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>ZIP</Label>
                                <Input
                                  value={bgCheckForm.address_zip}
                                  onChange={(e) => setBgCheckForm({ ...bgCheckForm, address_zip: e.target.value })}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Consent */}
                        <div className="bg-slate-50 rounded-xl p-4">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                              checked={bgCheckForm.consent}
                              onCheckedChange={(checked) => setBgCheckForm({ ...bgCheckForm, consent: checked })}
                              data-testid="bg-consent"
                            />
                            <div className="text-sm text-slate-600">
                              <p className="font-medium text-slate-900">I consent to a background check</p>
                              <p className="mt-1">
                                I authorize ElderLink and its partners to conduct a background check, 
                                including criminal records, employment history, and other relevant information 
                                as required for caregiver verification.
                              </p>
                            </div>
                          </label>
                        </div>

                        <Button
                          onClick={handleSubmitBackgroundCheck}
                          disabled={!bgCheckForm.consent || submittingBgCheck}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="submit-bg-check-btn"
                        >
                          <DollarSign className="w-4 h-4" />
                          {submittingBgCheck ? 'Processing...' : `Submit & Pay $${backgroundPackages[selectedPackage]?.price || '29.99'}`}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ID Verification Section - Only for Caregivers */}
        {isCaregiver && (
          <Card className="border-2 border-teal-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <Shield className="w-5 h-5 text-teal-600" />
                Identity Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {verificationStatus?.verification_status === 'approved' || verificationStatus?.trust_level === 'identity_verified' ? (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-teal-800">Identity Verified</h4>
                      <p className="text-sm text-teal-600">Your identity has been verified.</p>
                    </div>
                  </div>
                </div>
              ) : verificationStatus?.verification_status === 'pending' ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-800">Verification In Progress</h4>
                      <p className="text-sm text-amber-600">Your document is being reviewed.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger data-testid="document-type-select">
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="government_id">Government ID</SelectItem>
                          <SelectItem value="drivers_license">Driver's License</SelectItem>
                          <SelectItem value="passport">Passport</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Upload Document</Label>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      
                      {previewUrl ? (
                        <div className="relative">
                          <img 
                            src={previewUrl} 
                            alt="Document preview" 
                            className="w-full max-h-64 object-contain rounded-xl border border-slate-200"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                          >
                            <XCircle className="w-4 h-4" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-40 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-teal-500 hover:bg-teal-50 transition-colors"
                        >
                          <Camera className="w-6 h-6 text-slate-400" />
                          <span className="text-sm text-slate-600">Click to upload</span>
                        </button>
                      )}
                    </div>

                    <Button
                      onClick={handleUploadVerification}
                      disabled={!selectedFile || uploadingVerification}
                      className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingVerification ? 'Uploading...' : 'Submit for Verification'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Caregiver Profile */}
        {isCaregiver && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Caregiver Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Bio</Label>
                  <Textarea
                    placeholder="Tell families about yourself..."
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="min-h-[100px]"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      placeholder="25"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Years of Experience</Label>
                    <Input
                      type="number"
                      placeholder="5"
                      value={formData.experience_years}
                      onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Location</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      placeholder="San Francisco"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      placeholder="CA"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ZIP Code</Label>
                    <Input
                      placeholder="94105"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Services Offered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 sm:gap-2">
                  {SERVICES.map((service) => (
                    <label
                      key={service}
                      className={`flex items-center gap-1.5 sm:gap-2 px-2 py-1.5 sm:p-2.5 rounded-md border cursor-pointer transition-colors min-w-0 ${
                        formData.services.includes(service)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={formData.services.includes(service)}
                        onCheckedChange={() => handleServiceToggle(service)}
                        className="shrink-0"
                      />
                      <span className="text-xs sm:text-sm leading-tight">{service}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Languages Spoken</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 sm:gap-2">
                  {LANGUAGES.map((lang) => (
                    <label
                      key={lang}
                      className={`flex items-center gap-1.5 sm:gap-2 px-2 py-1.5 sm:p-2.5 rounded-md border cursor-pointer transition-colors min-w-0 ${
                        formData.languages.includes(lang)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={formData.languages.includes(lang)}
                        onCheckedChange={() => handleLanguageToggle(lang)}
                        className="shrink-0"
                      />
                      <span className="text-xs sm:text-sm leading-tight">{lang}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Subscription */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <CreditCard className="w-5 h-5" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {user?.subscription_tier === 'premium' ? 'Premium Plan' : 'Free Plan'}
                </p>
                <p className="text-sm text-slate-500">
                  {user?.subscription_tier === 'premium' ? 'Full access' : 'Limited features'}
                </p>
              </div>
              {user?.subscription_tier !== 'premium' && (
                <Link to="/subscription">
                  <Button className="bg-teal-500 hover:bg-teal-600 text-white">Upgrade</Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-rose-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-600 font-['Manrope']">
              <Trash2 className="w-5 h-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Sign Out</p>
                <p className="text-sm text-slate-500">Sign out of your account</p>
              </div>
              <Button variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={logout}>
                Sign Out
              </Button>
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-rose-600">Delete Account</p>
                  <p className="text-sm text-slate-500">Permanently delete your account and all data</p>
                </div>
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Account
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-rose-600">
                        <AlertTriangle className="w-5 h-5" />
                        Delete Account
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        This action is <strong>permanent and cannot be undone</strong>. All your data including shifts, medications, health metrics, and messages will be deleted.
                      </p>
                      <div>
                        <Label className="text-sm text-slate-700">
                          Type <strong>DELETE</strong> to confirm
                        </Label>
                        <Input
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="DELETE"
                          className="mt-1"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(''); }}>
                          Cancel
                        </Button>
                        <Button
                          className="bg-rose-600 hover:bg-rose-700 text-white"
                          disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                          onClick={async () => {
                            setDeletingAccount(true);
                            try {
                              await api.delete('/auth/account');
                              toast.success('Account deleted successfully');
                              logout();
                              navigate('/');
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Failed to delete account');
                            } finally {
                              setDeletingAccount(false);
                            }
                          }}
                        >
                          {deletingAccount ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                          Delete Permanently
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
